import type { QuestionnaireResponse, QuestionnaireResponseItem } from '@medplum/fhirtypes';
import {
  CLIENT_SIGNATURE_EXT_URL,
  getSignatureFromQR,
  type SignatureInfo,
} from '../pages/appointments/fhirNoteMapping';
import {
  getResponseItemDisplayMode,
  plainAnswerText,
  plainQuestionText,
  richHtmlForResponseItem,
} from './formResponseDisplay';
import { stripHtml } from '../pages/appointments/utils/stripHtml';
import { buildTextPdfWithOverlays, type PdfEmbeddedImage, type PdfLine, wrapLines, INDENT_CHARS } from './minimalTextPdf';
import {
  decodeBase64ImageToPngBytes,
  renderTypedSignatureToPngBytes,
} from './pdfImageDecode';
import { overlaySignatureImages } from './formResponsePdfOverlay';
import { formatSignatureDateTime } from './clientSignatureBlockHtml';

const SIGNATURE_GROUP = 'client-signature';

function wrapAnswerLines(answer: string, indent: number): PdfLine[] {
  const maxChars = Math.max(24, 78 - Math.floor(indent / INDENT_CHARS) * 12);
  return wrapLines(answer, maxChars).map((text) => ({
    text,
    style: 'body' as const,
    indent,
  }));
}

function flattenResponseItems(items: QuestionnaireResponseItem[], depth = 0): PdfLine[] {
  const lines: PdfLine[] = [];
  const indent = depth * INDENT_CHARS;

  for (const item of items) {
    const hasChildren = (item.item ?? []).length > 0;
    if (hasChildren) {
      const groupLabel = plainQuestionText(item);
      if (groupLabel) {
        if (lines.length > 0) lines.push({ text: '', style: 'body' });
        lines.push({ text: groupLabel, style: 'heading', indent });
      }
      lines.push(...flattenResponseItems(item.item ?? [], depth + 1));
      continue;
    }

    if (getResponseItemDisplayMode(item) === 'rich-html') {
      const htmlText = stripHtml(richHtmlForResponseItem(item)).replace(/\s+/g, ' ').trim();
      if (htmlText) {
        lines.push(...wrapAnswerLines(htmlText, indent));
      }
      continue;
    }

    const question = plainQuestionText(item);
    const answer = plainAnswerText(item);
    if (question) {
      lines.push({ text: question, style: 'heading', indent });
    }
    if (answer) {
      lines.push(...wrapAnswerLines(answer, indent + INDENT_CHARS));
    } else if (question) {
      lines.push({ text: '—', style: 'body', indent: indent + INDENT_CHARS });
    }
    lines.push({ text: '', style: 'body' });
  }

  while (lines.length > 0 && !lines[lines.length - 1]?.text) {
    lines.pop();
  }
  return lines;
}

async function resolveSignatureImage(signature: SignatureInfo): Promise<PdfEmbeddedImage | undefined> {
  if (signature.signatureData?.trim()) {
    try {
      const decoded = await decodeBase64ImageToPngBytes(
        signature.signatureData,
        signature.signatureFormat ?? 'image/png',
      );
      return {
        width: decoded.width,
        height: decoded.height,
        displayHeight: 60,
        overlayPngBytes: decoded.png,
      };
    } catch {
      /* fall through to typed render */
    }
  }

  const label = signature.typedText?.trim() || signature.name?.trim();
  if (!label) return undefined;

  const rendered = renderTypedSignatureToPngBytes(label);
  return {
    width: rendered.width,
    height: rendered.height,
    displayHeight: 60,
    overlayPngBytes: rendered.png,
  };
}

async function clientSignaturePdfLines(qr: QuestionnaireResponse): Promise<PdfLine[]> {
  const signature = getSignatureFromQR(qr, CLIENT_SIGNATURE_EXT_URL);
  if (!signature?.name?.trim() && !signature?.signatureData?.trim()) {
    return [];
  }

  const image = await resolveSignatureImage(signature);
  if (!image) return [];

  return [
    {
      text: '',
      group: SIGNATURE_GROUP,
      signatureBlock: {
        label: 'Client signature',
        image,
        signedAtLabel: signature.signedAt ? formatSignatureDateTime(signature.signedAt) : undefined,
      },
    },
  ];
}

export async function buildFormResponsePdfBytes(
  title: string,
  qr: QuestionnaireResponse,
): Promise<Uint8Array> {
  const lines: PdfLine[] = [];

  if (qr.authored) {
    lines.push({
      text: `Completed: ${new Date(qr.authored).toLocaleDateString('en-US', { dateStyle: 'long' })}`,
      style: 'meta',
    });
    lines.push({ text: '', style: 'body' });
  }

  lines.push(...flattenResponseItems(qr.item ?? []));
  lines.push(...(await clientSignaturePdfLines(qr)));

  const { bytes, overlays } = buildTextPdfWithOverlays(title, lines);
  return overlaySignatureImages(bytes, overlays);
}
